import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, Calendar, Thermometer, Leaf, Shield, Share2, Mail, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import BatchTimeline from '../components/traceability/BatchTimeline';
import TraceabilityMap from '../components/traceability/TraceabilityMap';
import EnvironmentalMetrics from '../components/metrics/EnvironmentalMetrics';
import SustainabilityBadge from '../components/common/SustainabilityBadge';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BatchDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [events, setEvents] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBatchData();
  }, [id]);

  const fetchBatchData = async () => {
    try {
      const [batchRes, eventsRes, metricsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/batches/${id}`),
        axios.get(`${BACKEND_URL}/api/batches/${id}/events`),
        axios.get(`${BACKEND_URL}/api/batches/${id}/metrics`),
      ]);
      setBatch(batchRes.data);
      setEvents(eventsRes.data);
      setMetrics(metricsRes.data);
    } catch (error) {
      console.error('Error fetching batch details:', error);
      toast.error('Failed to load batch details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NO', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleShareLink = () => {
    const verifyUrl = `${window.location.origin}/verify/${id}`;
    navigator.clipboard.writeText(verifyUrl);
    toast.success('Verification link copied to clipboard!');
  };

  const handleEmailShare = () => {
    const verifyUrl = `${window.location.origin}/verify/${id}`;
    const subject = encodeURIComponent(`PureSustain Batch Verification: ${batch.batch_number}`);
    const body = encodeURIComponent(
      `Hi,\n\nI'm sharing the complete supply chain verification for:\n\n` +
      `Product: ${batch.product_name}\n` +
      `Batch: ${batch.batch_number}\n` +
      `Origin: ${batch.origin.name}, ${batch.origin.region}\n` +
      `Integrity Score: ${batch.integrity_score}/100\n\n` +
      `View full traceability and compliance records:\n${verifyUrl}\n\n` +
      `This link provides complete transparency including:\n` +
      `• Chain of custody from catch to delivery\n` +
      `• Environmental metrics (CO₂, temperature)\n` +
      `• Compliance certifications (ISO 14001, CSRD, etc.)\n• Blockchain-verified records\n\n` +
      `Best regards,\n${user.name}\n` +
      `PureSustain - Supply Chain Transparency Platform`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading batch details...</p>
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Batch not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="batch-detail-page">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/batches')}
        className="mb-4"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Batches
      </Button>

      {/* Header */}
      <div className="bg-white rounded-xl p-8 border border-slate-200">
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Package className="w-8 h-8 text-blue-600" />
              <span className="text-lg font-mono text-slate-600">{batch.batch_number}</span>
            </div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope' }}>
              {batch.product_name}
            </h1>
            <p className="text-lg text-slate-600 italic">{batch.species}</p>
            
            {/* Share Buttons */}
            <div className="mt-4 flex gap-2">
              <Button
                onClick={handleShareLink}
                variant="outline"
                size="sm"
                data-testid="share-link-button"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
              <Button
                onClick={handleEmailShare}
                variant="outline"
                size="sm"
                data-testid="email-share-button"
              >
                <Mail className="w-4 h-4 mr-2" />
                Share via Email
              </Button>
            </div>
          </div>
          <div className="flex flex-col items-end gap-4">
            <div className="text-center">
              {batch.blockchain_verified && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-300 rounded-lg mb-2">
                  <Shield className="w-5 h-5 text-slate-700" />
                  <span className="text-sm font-medium text-slate-700">Blockchain-Verified Records</span>
                </div>
              )}
              <TooltipProvider>
                <div className="mt-2">
                  <div className="flex items-center gap-2 justify-center mb-1">
                    <span className="text-3xl font-bold text-slate-800">{batch.integrity_score}</span>
                    <span className="text-slate-600">/100</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-4 h-4 text-slate-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-sm">
                          Integrity Score reflects data completeness, event verification, and traceability continuity within the PureSustain platform. The score may be affected by missing data, delayed events, or manual overrides. This score is not a certification or regulatory guarantee.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-sm text-slate-500">Integrity Score</p>
                  <p className="text-xs text-slate-400 mt-1">Based on data completeness and verification</p>
                </div>
              </TooltipProvider>
            </div>
            
            {/* QR Code */}
            <div className="bg-white p-3 rounded-lg border border-slate-200">
              <QRCodeSVG 
                value={`${window.location.origin}/verify/${id}`} 
                size={120}
                level="M"
              />
              <p className="text-xs text-slate-500 text-center mt-2">Scan to verify</p>
            </div>
          </div>
        </div>

        {/* Quick Info Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <div className="flex items-center gap-2 text-slate-600 mb-1">
              <MapPin className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Origin</span>
            </div>
            <p className="text-base font-semibold text-slate-900">{batch.origin.name}</p>
            <p className="text-sm text-slate-500">{batch.origin.region}</p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-slate-600 mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Catch Date</span>
            </div>
            <p className="text-base font-semibold text-slate-900">{formatDate(batch.catch_date)}</p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-slate-600 mb-1">
              <Package className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Weight</span>
            </div>
            <p className="text-base font-semibold text-slate-900">{batch.weight_kg.toLocaleString()} kg</p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-slate-600 mb-1">
              <Leaf className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Total CO₂</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs cursor-help">
                      Estimated (PoC)
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Data provenance: Estimated (PoC)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-base font-semibold text-slate-900">{batch.total_co2_kg} kg</p>
          </div>
        </div>

        {/* Compliance Badges */}
        <div className="mt-6 pt-6 border-t border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wider">Compliance Status</h3>
          <div className="flex flex-wrap gap-2">
            {batch.compliance.map((comp, idx) => (
              <SustainabilityBadge
                key={idx}
                standard={comp.standard}
                status={comp.status}
                description={comp.description}
                lastAudit={comp.last_audit}
              />
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3 italic">
            Compliance status reflects data completeness, traceability, and verification within the PureSustain platform. Regulatory certification remains the responsibility of accredited authorities.
          </p>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white rounded-xl p-8 border border-slate-200">
        <h2 className="text-2xl font-semibold text-slate-800 mb-6" style={{ fontFamily: 'Manrope' }}>
          Supply Chain Route
        </h2>
        <TraceabilityMap events={events} origin={batch.origin} />
      </div>

      {/* Timeline and Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Timeline */}
        <div className="bg-white rounded-xl p-8 border border-slate-200">
          <h2 className="text-2xl font-semibold text-slate-800 mb-6" style={{ fontFamily: 'Manrope' }}>
            Supply Chain Events
          </h2>
          <BatchTimeline events={events} />
        </div>

        {/* Environmental Metrics */}
        <div className="bg-white rounded-xl p-8 border border-slate-200">
          <h2 className="text-2xl font-semibold text-slate-800 mb-6" style={{ fontFamily: 'Manrope' }}>
            Environmental Metrics
          </h2>
          <EnvironmentalMetrics metrics={metrics} />
        </div>
      </div>
    </div>
  );
};

export default BatchDetail;
